/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type { AbsoluteURL } from '@poweralpha/common';
import type { WindowListingPaths } from '../desktop/app-state/windowListing';

/** PlantSim pages rendered by the PlantSim window (`PlantSimEntry`), keyed by view. */
export const PLANT_ENTRY_PATHS = {
  results: '/plant-v2',
  result: '/plant-v2/results',
  resultsCompare: '/plant-v2/compare',
  sweep: '/plant-v2/sweep',
  batchSimulate: '/plant-v2/batch/simulate',
  batchResults: '/plant-v2/batch/results',
  configurations: '/plant-v2/configurations',
  configurationsCompare: '/plant-v2/configurations/compare',
  simulate: '/plant-v2/simulate',
  /** Old Net Benefit app pages (`/plant-v2/net-benefit/*`); they redirect to sweep results. */
  netBenefit: '/plant-v2/net-benefit',
} as const satisfies Record<string, AbsoluteURL>;

/** PlantSim paths of the window listing views (`plant.listing`). */
export const PLANT_LISTING_PATHS: WindowListingPaths = {
  results: PLANT_ENTRY_PATHS.results,
  results_compare: PLANT_ENTRY_PATHS.resultsCompare,
  configurations: PLANT_ENTRY_PATHS.configurations,
  configurations_compare: PLANT_ENTRY_PATHS.configurationsCompare,
};

export type PlantView = keyof typeof PLANT_ENTRY_PATHS;
/** Views with the Results/Config switch; every other view sits below one of them. */
export type PlantListing = 'results' | 'configurations';

/** Search param of the Results listing that filters it to single runs or sweeps. */
export const PLANT_RESULT_TYPE_PARAM = 'type';

/** The Results listing filtered to sweeps. */
export const PLANT_SWEEPS_PATH: AbsoluteURL = `${PLANT_ENTRY_PATHS.results}?${PLANT_RESULT_TYPE_PARAM}=sweep`;

/** A sweep's result page. */
export const getPlantSweepPath = (id: string): AbsoluteURL => `${PLANT_ENTRY_PATHS.sweep}?id=${id}`;

const viewByPath = new Map<string, PlantView>(
  Object.entries(PLANT_ENTRY_PATHS).map(([view, path]) => [path, view as PlantView]),
);

/** The view at `pathname` (a trailing slash is ignored); `undefined` outside the PlantSim window. */
export function getPlantView(pathname: string): PlantView | undefined {
  const path = pathname.length > 1 ? pathname.replace(/\/$/, '') : pathname;
  if (path.startsWith(`${PLANT_ENTRY_PATHS.netBenefit}/`)) return 'netBenefit';
  return viewByPath.get(path);
}

export const isPlantListing = (view: PlantView): view is PlantListing =>
  view === 'results' || view === 'configurations';

/**
 * The listing a view sits below (its breadcrumb parent). A simulate page opened from a result
 * (`?scenario=` without `?config=`) belongs to Results, any other one to Configurations.
 */
export function getPlantListing(view: PlantView, search: string): PlantListing {
  switch (view) {
    case 'configurations':
    case 'configurationsCompare':
      return 'configurations';
    case 'simulate': {
      const params = new URLSearchParams(search);
      return params.has('scenario') && !params.has('config') ? 'results' : 'configurations';
    }
    default:
      return 'results';
  }
}
